import { useCallback } from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";

export type ResourceType = "base" | "open" | "plan" | "boxlayout" | "mode-only";

export type RouteState = {
  /** What kind of resource is identified in the URL path */
  resourceType: ResourceType;

  /** Base chart ID (from /base/:chartId) */
  chartId: string | null;

  /** Plan ID (from /plan/:planId) */
  planId: string | null;

  /** Custom chart directory (from /open/directory/file) */
  directory: string | null;

  /** Custom chart file (from /open/directory/file) */
  file: string | null;

  /** Box layout key (from /boxlayout/:layoutKey) */
  layoutKey: string | null;

  /** UI mode — from ?mode= query param, or derived from legacy route path */
  mode: string;

  /** Right sidebar tool — from ?tool= */
  tool: string | null;

  /** Active view ID — from ?view= */
  view: string | null;

  /** Active background filename — from ?background= */
  background: string | null;

  /** Subchart top person ID — from ?topOfChart= */
  topOfChart: string | null;

  /** Whether chart explorer modal should be open — from ?chartexplorer */
  chartExplorer: boolean;

  /** Subchart depth — from ?levels= */
  levels: string | null;

  /** Canvas zoom percent — from ?zoom= */
  zoom: string | null;

  /** Set a single query param (pass null to remove) */
  setParam: (key: string, value: string | null) => void;

  /** Set multiple query params at once */
  setParams: (params: Record<string, string | null>) => void;
};

/**
 * Derives the resource type from the current pathname.
 * Pathname is relative to the /app basename.
 */
function getResourceType(pathname: string): ResourceType {
  if (pathname.startsWith("/base/")) return "base";
  if (pathname.startsWith("/open/")) return "open";
  if (pathname.startsWith("/plan/")) return "plan";
  if (pathname.startsWith("/boxlayout/")) return "boxlayout";
  return "mode-only";
}

/**
 * For legacy mode-only routes (/chart, /edit, /planning, etc.),
 * derive the mode from the pathname segment.
 */
function getModeFromPath(pathname: string): string {
  const [, segment] = pathname.split("/");
  switch (segment) {
    case "edit":
      return "edit";
    case "planning":
    case "plan":
      return "planning";
    case "directory":
      return "directory";
    case "boxlayouteditor":
      return "edit";
    default:
      return "chart";
  }
}

/**
 * For /open/* routes, parse the wildcard into directory and file.
 * Examples:
 *   "MyFolder/chart.occ"     → { directory: "MyFolder", file: "chart.occ" }
 *   "Masters/Name.ocb"       → { directory: "Masters", file: "Name.ocb" }
 *   "Foo/Bar/Baz.ocb"        → { directory: "Foo/Bar", file: "Baz.ocb" }
 */
function parseOpenPath(wildcard: string | undefined): {
  directory: string | null;
  file: string | null;
} {
  if (!wildcard) return { directory: null, file: null };
  const lastSlash = wildcard.lastIndexOf("/");
  if (lastSlash === -1) return { directory: null, file: wildcard };
  return {
    directory: wildcard.substring(0, lastSlash),
    file: wildcard.substring(lastSlash + 1)
  };
}

export const useRouteState = (): RouteState => {
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const resourceType = getResourceType(location.pathname);

  // Resource identity from route params
  const chartId = params.chartId ?? null;
  const planId = params.planId ?? null;
  const layoutKey = params.layoutKey ?? null;

  // For /open/* routes, parse directory/file from the wildcard
  const openPath = parseOpenPath(params["*"]);

  // Mode: query param takes precedence, then derive from path
  const modeParam = searchParams.get("mode");
  const mode = modeParam ?? getModeFromPath(location.pathname);

  // UI state from query params
  const tool = searchParams.get("tool");
  const view = searchParams.get("view");
  const background = searchParams.get("background");
  const topOfChart = searchParams.get("topOfChart");
  // location.search (committed), not searchParams, because useSearchParams
  // transiently surfaces a useBlocker-held nav's pending ?chartexplorer and would
  // open the modal before the URL commits.
  const chartExplorer = new URLSearchParams(location.search).has("chartexplorer");
  const levels = searchParams.get("levels");
  const zoom = searchParams.get("zoom");

  const setParam = useCallback(
    (key: string, value: string | null) => {
      setSearchParams(prev => {
        if (value === null) {
          prev.delete(key);
        } else {
          prev.set(key, value);
        }
        return prev;
      });
    },
    [setSearchParams]
  );

  const setParams = useCallback(
    (params: Record<string, string | null>) => {
      setSearchParams(prev => {
        for (const [key, value] of Object.entries(params)) {
          if (value === null) {
            prev.delete(key);
          } else {
            prev.set(key, value);
          }
        }
        return prev;
      });
    },
    [setSearchParams]
  );

  return {
    resourceType,
    chartId,
    planId,
    directory: openPath.directory,
    file: openPath.file,
    layoutKey,
    mode,
    tool,
    view,
    background,
    topOfChart,
    chartExplorer,
    levels,
    zoom,
    setParam,
    setParams
  };
};
